/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  ListItemIcon,
  Tooltip,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  Menu as MenuIcon,
  Search as SearchIcon,
  Close as ClearSearchIcon,
  AddBox as AddBoxIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { ConfirmationModal } from '../Modal/ConfirmationModal';
import { EditTextModal } from '../Modal/EditTextModal';

const SIDEBAR_WIDTH = 280;
const COLLAPSED_WIDTH = 56;
const FADE_TRANSITION = 'opacity 150ms ease';

const toggleIconSx = { color: 'text.secondary' };

const historyItemSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  borderRadius: '10px',
  mb: 0.5,
  '&.Mui-selected': {
    bgcolor: 'rgba(132, 216, 197, 0.16)',
    '&:hover': { bgcolor: 'rgba(132, 216, 197, 0.24)' },
  },
  '& .history-menu-btn': { opacity: 0 },
  '&:hover .history-menu-btn, &:focus-within .history-menu-btn, &.Mui-selected .history-menu-btn':
    {
      opacity: 1,
    },
};

const historyItemTextSx = { color: 'text.primary' };

const historyItemDateSx = { color: 'rgba(255, 255, 255, 0.5)' };

const menuButtonSx = {
  color: 'rgba(255, 255, 255, 0.4)',
  transition: 'opacity 120ms ease',
  '&:hover': { color: 'text.primary' },
};

export const ChatSidebar = ({
  open,
  onToggle,
  sessions,
  activeSessionId,
  onSelectSession,
  onNewChat,
  onRenameSession,
  onDeleteSession,
}) => {
  const [search, setSearch] = useState('');
  const [sessionPendingDelete, setSessionPendingDelete] = useState(null);
  const [sessionPendingRename, setSessionPendingRename] = useState(null);
  const [menuSession, setMenuSession] = useState(null);
  const [renameOpenCount, setRenameOpenCount] = useState(0);

  const filteredSessions = sessions.filter((session) =>
    session.title.toLowerCase().includes(search.toLowerCase())
  );

  const handleConfirmDelete = () => {
    onDeleteSession(sessionPendingDelete.id);
    setSessionPendingDelete(null);
  };

  const handleConfirmRename = (newTitle) => {
    if (newTitle !== sessionPendingRename.title) {
      onRenameSession(sessionPendingRename.id, newTitle);
    }
    setSessionPendingRename(null);
  };

  const openMenu = (e, session) => {
    e.stopPropagation();
    setMenuSession({ id: session.id, anchorEl: e.currentTarget });
  };

  const closeMenu = () => setMenuSession(null);

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          width: open ? SIDEBAR_WIDTH : COLLAPSED_WIDTH,
          flexShrink: 0,
          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
          borderRight: '1px solid rgba(255, 255, 255, 0.08)',
          bgcolor: 'rgba(255, 255, 255, 0.02)',
          transition: 'width 220ms cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {/* Collapsed rail */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: COLLAPSED_WIDTH,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pt: 2,
            opacity: open ? 0 : 1,
            pointerEvents: open ? 'none' : 'auto',
            transition: FADE_TRANSITION,
          }}
        >
          <Tooltip title="Expand sidebar" placement="right">
            <IconButton
              aria-label="Expand sidebar"
              onClick={onToggle}
              sx={toggleIconSx}
              tabIndex={open ? -1 : 0}
            >
              <MenuIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Expanded content */}
        <Box
          sx={{
            width: SIDEBAR_WIDTH,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            px: 2,
            py: 2,
            opacity: open ? 1 : 0,
            pointerEvents: open ? 'auto' : 'none',
            transition: FADE_TRANSITION,
          }}
        >
          {/* EPS Assistant + Toggle */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pb: 2,
            }}
          >
            <Typography variant="subtitle1" sx={{ color: 'text.primary' }}>
              EPS Assistant
            </Typography>
            <Tooltip title="Collapse sidebar">
              <IconButton
                aria-label="Collapse sidebar"
                size="small"
                onClick={onToggle}
                sx={toggleIconSx}
                tabIndex={open ? 0 : -1}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Search chat */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search chats"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    fontSize="small"
                    sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
                  />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <Tooltip title="Clear search">
                    <IconButton
                      aria-label="Clear search"
                      size="small"
                      onClick={() => setSearch('')}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        '&:hover': { color: 'text.primary' },
                      }}
                    >
                      <ClearSearchIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 1.5,
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                color: 'text.primary',
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.24)' },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.4)',
                },
                '&.Mui-focused fieldset': { borderColor: 'text.secondary' },
              },
              '& .MuiOutlinedInput-input::placeholder': {
                color: 'rgba(255, 255, 255, 0.5)',
                opacity: 1,
              },
            }}
          />

          {/* New chat */}
          <Button
            fullWidth
            startIcon={<AddBoxIcon />}
            onClick={onNewChat}
            sx={{
              mb: 2,
              textTransform: 'none',
              fontWeight: 500,
              color: 'text.primary',
              bgcolor: 'text.secondary',
              '&:hover': { bgcolor: '#6fc9b8' },
              '&.MuiButton-root': { justifyContent: 'flex-start', gap: '15px' },
            }}
          >
            New Chat
          </Button>

          {/* Chat history list */}
          <List
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(255, 255, 255, 0.25) transparent',
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(255, 255, 255, 0.25)',
                borderRadius: '3px',
              },
            }}
          >
            {filteredSessions.length === 0 ? (
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.5)',
                  textAlign: 'center',
                  pt: 2,
                }}
              >
                No matching chats.
              </Typography>
            ) : (
              filteredSessions.map((session) => (
                <ListItemButton
                  key={session.id}
                  selected={session.id === activeSessionId}
                  onClick={() => onSelectSession(session)}
                  sx={historyItemSx}
                >
                  <Tooltip
                    title={session.title}
                    placement="right"
                    arrow
                    enterDelay={400}
                  >
                    <ListItemText
                      primary={session.title}
                      secondary={session.date}
                      primaryTypographyProps={{
                        noWrap: true,
                        sx: historyItemTextSx,
                      }}
                      secondaryTypographyProps={{
                        variant: 'caption',
                        sx: historyItemDateSx,
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="Chat options">
                    <IconButton
                      aria-label={`Chat options for ${session.title}`}
                      className="history-menu-btn"
                      size="small"
                      onClick={(e) => openMenu(e, session)}
                      sx={menuButtonSx}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItemButton>
              ))
            )}
          </List>
        </Box>
      </Box>

      <Menu
        anchorEl={menuSession?.anchorEl}
        open={!!menuSession}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { borderRadius: '10px', minWidth: 160 } }}
      >
        <MenuItem
          onClick={() => {
            const session = sessions.find((s) => s.id === menuSession.id);
            setSessionPendingRename(session);
            setRenameOpenCount((count) => count + 1);
            closeMenu();
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          Rename
        </MenuItem>
        <MenuItem
          onClick={() => {
            const session = sessions.find((s) => s.id === menuSession.id);
            setSessionPendingDelete(session);
            closeMenu();
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          Delete
        </MenuItem>
      </Menu>

      <EditTextModal
        key={renameOpenCount}
        open={!!sessionPendingRename}
        onCancel={() => setSessionPendingRename(null)}
        title="Rename Chat"
        initialText={sessionPendingRename?.title ?? ''}
        maxLength={120}
        confirmLabel="Save"
        onConfirm={handleConfirmRename}
      />

      <ConfirmationModal
        open={!!sessionPendingDelete}
        onClose={() => setSessionPendingDelete(null)}
        title="Delete Chat"
        desc={
          sessionPendingDelete
            ? `Are you sure you want to delete "${sessionPendingDelete.title}"? This can't be undone.`
            : ''
        }
        primaryClickHandler={handleConfirmDelete}
        secondaryClickHandler={() => setSessionPendingDelete(null)}
        primaryBtnName="Delete"
        secondaryBtnName="Cancel"
      />
    </>
  );
};

ChatSidebar.propTypes = {
  open: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  sessions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      title: PropTypes.string.isRequired,
      date: PropTypes.string,
    })
  ).isRequired,
  activeSessionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onSelectSession: PropTypes.func.isRequired,
  onNewChat: PropTypes.func.isRequired,
  onRenameSession: PropTypes.func.isRequired,
  onDeleteSession: PropTypes.func.isRequired,
};
